import { zodResolver } from '@hookform/resolvers/zod';
import { Slider } from '@miblanchard/react-native-slider';
import { router } from 'expo-router';
import type { PhoneNumber } from 'libphonenumber-js';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useEffect, useMemo, useState } from 'react';
import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';
import { z } from 'zod';

import type { Car } from '@/api/cars/types';
import { useCarDetailOptions } from '@/api/partners/cars/use-car-detail-options';
import { useLocations } from '@/api/partners/locations/use-locations';
import { MODEL_MINIMUM_RATES } from '@/components/cars';
import { InputPhone } from '@/components/input-phone';
import type { PhoneCountry } from '@/components/input-phone/types';
import { MapView } from '@/components/map-view';
import {
  BASE_DELIVERY_EARNINGS,
  getEstimatedMaxDeliveryFee,
  translate,
  validatePhoneNumber,
} from '@/core';
import { GREY_10, GREY_30, GREY_60, PINKLING } from '@/style/colors';
import { Button, ControlledInput, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { CardItem } from '@/ui/card-item';
import { CheckItem } from '@/ui/check-item';
import type { DropdownOption } from '@/ui/dropdown';
import Dropdown from '@/ui/dropdown';
import { Icon } from '@/ui/icon';
import { Car as CarIcon } from '@/ui/icons';
import { Info } from '@/ui/icons';
import { Dollar } from '@/ui/icons/dollar';
import { Sparkles } from '@/ui/icons/sparkles';
import { Loader } from '@/ui/loader';

import type { CarDetailsFormType } from '../car-details-form';

type PricingFormProps = {
  // dailyRate: number;
  // oneDayMultiplier?: number;
  // defaultLocationId?: number;
};

const pricingFormSchema = z.object({
  useAutomatedPricing: z.boolean(),

  // manual pricing
  dailyRate: z.string().optional(),

  // automated pricing
  minimumDailyRate: z.string().optional(),
  // maximumDailyRate: z.string().optional(),

  oneDayMultiplier: z.string().optional(),
  defaultLocationId: z
    .string()
    .min(1, 'Please select a location from the dropdown list.'),
  minDaysToBook: z.string().optional(),
  hoursBetweenTrips: z.string().optional(),
  minHoursInAdvance: z.string().optional(),
  supportsDelivery: z.boolean().optional(),
  maxDeliveryDistance: z.string().optional(),
  deliveryPersonPhone: z.string().optional(),
});

function createPricingFormSchema(modelName: string) {
  return pricingFormSchema.superRefine((data, ctx) => {
    /* ----------------------------
     * Required fields by mode
     * ---------------------------- */
    if (data.useAutomatedPricing) {
      if (!data.minimumDailyRate) {
        ctx.addIssue({
          path: ['minimumDailyRate'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }

      // if (!data.maximumDailyRate) {
      //   ctx.addIssue({
      //     path: ['maximumDailyRate'],
      //     message: 'Required',
      //     code: z.ZodIssueCode.custom,
      //   });
      // }
    } else {
      if (!data.dailyRate) {
        ctx.addIssue({
          path: ['dailyRate'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
    }

    /* ----------------------------
     * Model-specific minimums
     * ---------------------------- */
    if (data.useAutomatedPricing && data.minimumDailyRate) {
      const minRate = Number(data.minimumDailyRate);
      if (Number.isNaN(minRate)) return;

      const modelMinimums = MODEL_MINIMUM_RATES;

      const requiredMin = modelMinimums[modelName];

      if (requiredMin && minRate < requiredMin) {
        ctx.addIssue({
          path: ['minimumDailyRate'],
          message: `Minimum rate for ${modelName} is $${requiredMin}`,
          code: z.ZodIssueCode.custom,
        });
      }
    }

    /* ----------------------------
     * Min <= Max
     * ---------------------------- */
    // if (
    //   data.useAutomatedPricing &&
    //   data.minimumDailyRate
    //   // && data.maximumDailyRate
    // ) {
    //   const min = Number(data.minimumDailyRate);
    //   const max = Number(data.maximumDailyRate);

    //   if (!Number.isNaN(min)) {
    //     // if (!Number.isNaN(min) && !Number.isNaN(max) && min > max) {
    //     ctx.addIssue({
    //       path: ['maximumDailyRate'],
    //       message: 'Maximum rate must be greater than minimum rate',
    //       code: z.ZodIssueCode.custom,
    //     });
    //   }
    // }
  });
}

export type PricingFormType = z.infer<typeof pricingFormSchema>;

export default function PricingForm({
  car,
  name,
  onChange,
  onValidChange,
  onErrorsChange,
}: {
  car: Car;
  name: string;
  onChange: (name: string, values: CarDetailsFormType) => void;
  onValidChange: (name: string, isValid: boolean) => void;
  onErrorsChange: (name: string, errors: any) => void;
}) {
  const { styles, theme } = useStyles(stylesheet);
  const [addOneDayUpcharge, setAddOneDayUpcharge] = useState(
    car.oneDayMultiplier !== null
  );
  const [addDeliveryAgent, setAddDeliveryAgent] = useState(
    car.deliveryPersonPhone !== ''
  );
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<PhoneCountry>();
  const [mapZoom, setMapZoom] = useState(13);
  const [mapAltitude, setMapAltitude] = useState(3000);
  const { data: locationsData, refetch: refetchLocations } = useLocations();

  const schema = useMemo(
    () => createPricingFormSchema(car.modelName),
    [car.modelName]
  );

  const {
    control,
    trigger,
    setValue,
    getValues,
    formState: { isValid, errors },
    watch,
  } = useForm<PricingFormType>({
    defaultValues: {
      useAutomatedPricing: car.useAutomatedPricing,
      dailyRate: car.dailyRate?.toString(),
      minimumDailyRate: car.minimumDailyRate?.toString(),
      // maximumDailyRate: car.maximumDailyRate?.toString(),
      oneDayMultiplier: car.oneDayMultiplier
        ? (car.oneDayMultiplier * 100).toString()
        : undefined,
      defaultLocationId: car.defaultLocation?.id.toString() || '',
      minDaysToBook: car.minDaysToBook?.toString() || '1',
      hoursBetweenTrips: car.hoursBetweenTrips?.toString() || '3',
      minHoursInAdvance: car.minHoursInAdvance?.toString() || '3',
      supportsDelivery: car.supportsDelivery,
      maxDeliveryDistance: car.maxDeliveryDistance?.toString() || '10',
      deliveryPersonPhone: car.deliveryPersonPhone,
    },
    resolver: zodResolver(schema),
  });

  const {
    data: carDetailOptions,
    isPending: isLoadingCarDetailOptions,
    refetch: refetchCarDetailOptions,
  } = useCarDetailOptions({
    variables: { vins: [car.vin] },
  });

  useEffect(() => {
    onChange(name, getValues());
    trigger();
  }, []);

  useEffect(() => {
    onValidChange(name, isValid);
  }, [isValid]);

  useEffect(() => {
    onErrorsChange(name, errors);
  }, [JSON.stringify(errors)]);

  useEffect(() => {
    const sub = watch(() => {
      onChange(name, getValues());
    });
    return () => sub.unsubscribe();
  }, [watch]);

  const [
    useAutomatedPricing,
    maxDeliveryDistance,
    defaultLocationId,
    hoursBetweenTrips,
    minHoursInAdvance,
    supportsDelivery,
    deliveryPersonPhone,
  ] = useWatch({
    control,
    name: [
      'useAutomatedPricing',
      'maxDeliveryDistance',
      'defaultLocationId',
      'hoursBetweenTrips',
      'minHoursInAdvance',
      'supportsDelivery',
      'deliveryPersonPhone',
    ],
  });

  useEffect(() => {
    if (!maxDeliveryDistance) return;

    const distance = parseInt(maxDeliveryDistance.toString(), 10);

    let zoom = 13;
    if (distance <= 10) zoom = 10;
    else if (distance <= 20) zoom = 9.5;
    else if (distance <= 35) zoom = 9;
    else if (distance <= 50) zoom = 8.5;
    else if (distance <= 65) zoom = 8;
    else if (distance <= 85) zoom = 7.6;
    else if (distance <= 110) zoom = 7.3;
    else if (distance <= 130) zoom = 7;
    else zoom = 6.5;

    setMapZoom(zoom);

    let altitude = 3000;
    if (distance <= 10) altitude = 100000;
    else if (distance <= 20) altitude = 100000;
    else if (distance <= 35) altitude = 200000;
    else if (distance <= 50) altitude = 300000;
    else if (distance <= 65) altitude = 400000;
    else if (distance <= 85) altitude = 500000;
    else if (distance <= 110) altitude = 600000;
    else if (distance <= 130) altitude = 700000;
    else altitude = 1000000;
    setMapAltitude(altitude);
  }, [maxDeliveryDistance]);

  const parsedPhoneNumber = React.useMemo((): PhoneNumber | null => {
    if (!deliveryPersonPhone) return null;

    const phoneNumber = parsePhoneNumber(deliveryPersonPhone);

    return phoneNumber;
  }, [deliveryPersonPhone]);

  const deliveryCost = useMemo(
    () =>
      getEstimatedMaxDeliveryFee(
        maxDeliveryDistance,
        carDetailOptions?.deliveryDistanceCostOptions
      ),
    [carDetailOptions, maxDeliveryDistance]
  );

  const pickupLocation = useMemo(() => {
    if (defaultLocationId && locationsData) {
      const selected = locationsData.find(
        (location) => location.id.toString() === defaultLocationId
      );
      if (selected) return selected;
    }
    return car.defaultLocation;
  }, [defaultLocationId, locationsData, car.defaultLocation]);

  useEffect(() => {
    setPhone(parsedPhoneNumber?.nationalNumber ?? '');
  }, [parsedPhoneNumber]);

  const isValidPhoneNumber = useMemo(() => {
    if (!country || !phone) return false;

    return validatePhoneNumber(country, phone);
  }, [country, phone]);

  useEffect(() => {
    if (isValidPhoneNumber && phone && country) {
      setValue('deliveryPersonPhone', `+${country?.callingCode}${phone}`);
    } else {
      setValue('deliveryPersonPhone', '');
    }
  }, [isValidPhoneNumber, phone, country]);

  if (!locationsData) {
    return (
      <View style={{ flex: 1, backgroundColor: theme.colors.background_1 }}>
        <View
          style={{
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Loader style={{ height: 250, width: 250 }} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.contentContainer}>
        <View style={styles.pricingContainer}>
          <View style={{ gap: 16 }}>
            <Text type="title2">Pricing</Text>

            <Alert
              type={AlertType.BRAND}
              title="Pro Tip: Set competitive rates"
              body="Keep in mind that pricing the car too high will mean your car ranks lower than more affordable options in search results."
              icon={<Info />}
            />

            <View style={styles.manualAutomatedContainer}>
              <CardItem
                icon={
                  <Icon
                    type="dressed"
                    icon={<Sparkles />}
                    fillColor={theme.colors.icon.color}
                    width={24}
                    height={24}
                  />
                }
                title={'Automated Pricing'}
                body={
                  'Beta Feature - this will automatically set your rates based on demand to maximize your rentals and earnings'
                }
                style={styles.manualAutomatedCard}
                active={useAutomatedPricing === true}
                onPress={() => {
                  setValue('useAutomatedPricing', true);
                }}
              />

              <CardItem
                icon={
                  <Icon
                    type="dressed"
                    icon={<Dollar />}
                    fillColor={theme.colors.icon.color}
                    width={24}
                    height={24}
                  />
                }
                title={'Manual Pricing'}
                body={
                  'You can manually adjust your rates to optimize your rentals and earnings.'
                }
                style={styles.manualAutomatedCard}
                active={useAutomatedPricing === false}
                onPress={() => {
                  setValue('useAutomatedPricing', false);
                }}
              />
            </View>

            {useAutomatedPricing === false ? (
              <View>
                <ControlledInput
                  control={control}
                  name="dailyRate"
                  label="Daily Rate"
                  keyboardType="number-pad"
                  onBlur={() => {
                    trigger('dailyRate');
                  }}
                  onChangeText={(text) => {
                    setValue('dailyRate', text);
                    trigger('dailyRate');
                  }}
                  tooltipText="This will be the daily rate your car earns whenever on a trip. You'll keep 70% of the daily rate. Weekly and monthly trips are automatically discounted by 15% and 30% respectively."
                />
              </View>
            ) : null}

            {useAutomatedPricing === true ? (
              <View style={{ gap: 16 }}>
                <ControlledInput
                  control={control}
                  name="minimumDailyRate"
                  label="Minimum Rate"
                  keyboardType="number-pad"
                  onBlur={() => {
                    trigger('minimumDailyRate');
                  }}
                  onChangeText={(text) => {
                    setValue('minimumDailyRate', text);
                    trigger('minimumDailyRate');
                  }}
                  tooltipText="You'll keep 70% of the daily rate. Weekly and monthly trips are automatically discounted by 15% and 30% respectively."
                />

                {/* <ControlledInput
                  control={control}
                  name="maximumDailyRate"
                  label="Maximum Rate"
                  keyboardType="number-pad"
                  onBlur={() => {
                    trigger('maximumDailyRate');
                  }}
                  onChangeText={(text) => {
                    setValue('maximumDailyRate', text);
                    trigger('maximumDailyRate');
                  }}
                  tooltipText="You'll keep 70% of the daily rate. Weekly and monthly trips are automatically discounted by 15% and 30% respectively."
                /> */}
              </View>
            ) : null}

            <View style={{ gap: 16 }}>
              <CheckItem
                text="Add one day rental upcharge"
                caption="This will increase the price for 1-day rentals only. Note: this may lead to fewer reservations."
                onPress={() => {
                  if (addOneDayUpcharge === true) {
                    setValue('oneDayMultiplier', '');
                  }

                  setAddOneDayUpcharge(!addOneDayUpcharge);
                }}
                active={addOneDayUpcharge}
                type="checkbox"
              />

              {addOneDayUpcharge ? (
                <ControlledInput
                  placeholder="10%"
                  control={control}
                  name="oneDayMultiplier"
                  label="One day rental up-charge %"
                  keyboardType="number-pad"
                  onBlur={() => {
                    trigger('oneDayMultiplier');
                  }}
                  onChangeText={(text) => {
                    setValue('oneDayMultiplier', text);
                    trigger('oneDayMultiplier');
                  }}
                />
              ) : null}
            </View>
          </View>
        </View>

        <View style={styles.availabilityDeliveryContainer}>
          <View style={{ gap: 16 }}>
            <Text type="title2">Availability</Text>

            <View style={{ gap: 16 }}>
              {locationsData.length > 0 ? (
                <Dropdown
                  mode="single"
                  label="Select your car's pickup location"
                  options={locationsData.map((location) => ({
                    id: location.id.toString(),
                    value: location.id.toString(),
                    title: location.address,
                  }))}
                  placeHolderText="Select your car's pickup location"
                  onSelect={(selected: DropdownOption[]) => {
                    setValue('defaultLocationId', selected[0]?.id ?? '');
                    trigger();
                  }}
                  onClear={() => {
                    setValue('defaultLocationId', '');
                    trigger('defaultLocationId');
                  }}
                  onClose={() => {
                    trigger('defaultLocationId');
                  }}
                  error={errors.defaultLocationId?.message}
                  values={defaultLocationId ? [defaultLocationId] : []}
                />
              ) : null}

              <Button
                text="Add new location"
                type="secondaryExtraSmall"
                onPress={() => {
                  router.push('/partner/more/locations/add-location');
                }}
                style={{ width: 130 }}
              />
            </View>

            <ControlledInput
              placeholder="Enter a number"
              control={control}
              name="minDaysToBook"
              label="Minimum Rental Days"
              keyboardType="number-pad"
              onBlur={() => {
                trigger('minDaysToBook');
              }}
              onChangeText={(text) => {
                setValue('minDaysToBook', text);
                trigger('minDaysToBook');
              }}
              tooltipText="Your car will only appear in search results if the guest selects a trip that is this number of days or longer. We recommend setting this to 1"
            />

            <Dropdown
              mode="single"
              label="Minimum Time Between Rentals"
              options={[
                { id: '3', value: '3', title: '3 Hours' },
                { id: '6', value: '6', title: '6 Hours' },
                { id: '12', value: '12', title: '12 Hours' },
                { id: '24', value: '24', title: '24 Hours' },
              ]}
              // placeHolderText="Select your car's pickup location"
              onSelect={(selected: DropdownOption[]) => {
                setValue('hoursBetweenTrips', selected[0].id);
                trigger();
              }}
              tooltipText="Sets a buffer between the end of one trip and the start of the next. We hold the calendar for this time so you can clean, charge, or handle handoff. Existing reservations stay as is.
Example: 3 hours means a trip ending at 5 pm makes the next eligible start 8 pm."
              values={hoursBetweenTrips ? [hoursBetweenTrips.toString()] : []}
            />

            <Dropdown
              mode="single"
              label="Minimum Time In Advance"
              options={[
                { id: '3', value: '3', title: '3 Hours' },
                { id: '6', value: '6', title: '6 Hours' },
                { id: '12', value: '12', title: '12 Hours' },
                { id: '24', value: '24', title: '24 Hours' },
              ]}
              // placeHolderText="Select your car's pickup location"
              onSelect={(selected: DropdownOption[]) => {
                setValue('minHoursInAdvance', selected[0].id);
                trigger();
              }}
              tooltipText="Requires lead time for new bookings. Guests can only start a trip after this window from the moment they book. Higher values reduce last-minute bookings.
Example: with 3 hours, a booking made at 2 pm cannot start before 5 pm today."
              values={minHoursInAdvance ? [minHoursInAdvance?.toString()] : []}
            />
          </View>

          <View
            style={{
              gap: 16,
              paddingBottom: 32,
            }}
          >
            <Text type="title2">Delivery</Text>

            <CheckItem
              text="Available For Delivery"
              caption={translate('partner.cars.deliveryEarningsCaption', {
                amount: BASE_DELIVERY_EARNINGS.toFixed(2),
              })}
              onPress={() => {
                setValue('supportsDelivery', !supportsDelivery);
                trigger();
              }}
              active={supportsDelivery}
              type="checkbox"
            />

            {supportsDelivery && pickupLocation ? (
              <View style={{ gap: 8 }}>
                <Text type="bodyBold">Maximum delivery distance (miles)</Text>

                <View
                  style={{
                    height: 300,
                    width: '100%',
                    borderRadius: 16,
                    overflow: 'hidden',
                  }}
                >
                  <MapView
                    markers={[
                      {
                        latitude: pickupLocation.latitude,
                        longitude: pickupLocation.longitude,
                        icon: (
                          <Icon
                            type="naked"
                            icon={<CarIcon />}
                            fillColor={theme.colors.icon.color}
                            width={14}
                            height={14}
                          />
                        ),
                      },
                    ]}
                    defaultCenter={{
                      latitude: pickupLocation.latitude,
                      longitude: pickupLocation.longitude,
                    }}
                    fitBounds={false}
                    altitude={mapAltitude}
                    zoom={mapZoom}
                    showsUserLocation={false}
                    style={{ borderRadius: 16 }}
                    circles={[
                      {
                        center: {
                          latitude: pickupLocation.latitude,
                          longitude: pickupLocation.longitude,
                        },
                        radius: maxDeliveryDistance * 1609.34, // convert miles to meters
                        strokeColor: theme.colors.icon.brandColor,
                        strokeWidth: 2,
                        fillColor: theme.colors.chip.brand.backgroundColor,
                      },
                    ]}
                  />
                </View>

                <View>
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text type="title2">{maxDeliveryDistance} miles</Text>

                    <Text type="caption1">
                      Estimated max delivery fee: ${deliveryCost?.toFixed(2)}
                    </Text>
                  </View>

                  <Slider
                    value={maxDeliveryDistance}
                    onSlidingComplete={(values) => {
                      setValue('maxDeliveryDistance', values[0].toString());
                    }}
                    onValueChange={(values) => {
                      setValue('maxDeliveryDistance', values[0].toString());
                    }}
                    minimumValue={10}
                    maximumValue={150}
                    step={5}
                    minimumTrackTintColor={PINKLING}
                    maximumTrackTintColor={
                      UnistylesRuntime.themeName === 'dark' ? GREY_60 : GREY_30
                    }
                    thumbTintColor={
                      UnistylesRuntime.themeName === 'dark' ? GREY_10 : GREY_60
                    }
                  />
                </View>

                <View>
                  <CheckItem
                    text="Add Delivery Agent"
                    caption="Agents assigned to this car will also receive delivery instructions. They’ll get the car information, pickup and delivery addresses, guest details, and several reminders to deliver the car."
                    onPress={() => {
                      if (addDeliveryAgent === true) {
                        setValue('deliveryPersonPhone', '');
                      }

                      setAddDeliveryAgent(!addDeliveryAgent);
                    }}
                    active={addDeliveryAgent}
                    type="checkbox"
                  />
                </View>

                {addDeliveryAgent ? (
                  <View>
                    <InputPhone
                      onChangeText={(text: string) => setPhone(text)}
                      onCountryChange={(c: PhoneCountry) => setCountry(c)}
                      defaultCountryCode={parsedPhoneNumber?.country}
                      phone={parsedPhoneNumber?.nationalNumber}
                    />
                  </View>
                ) : null}
              </View>
            ) : null}
          </View>
        </View>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
    gap: 16,
  },
  contentContainer: {
    flexGrow: 1,
    gap: 24,
    justifyContent: {
      xs: 'space-between',
      md: 'flex-start',
    },
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
  },
  pricingContainer: {
    flex: {
      md: 1,
    },
    gap: 32,
    paddingBottom: 32,
  },
  manualAutomatedContainer: {
    flex: 1,
    gap: 16,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
  },
  manualAutomatedCard: {
    flex: {
      md: 1,
    },
    width: {
      xs: '100%',
      md: 'auto',
    },
  },
  availabilityDeliveryContainer: {
    flex: 1,
    gap: 32,
  },
}));
